import { useEffect, useState } from 'react';
import { api } from './api.ts';
import {
  chatModelOptions,
  type ChatEngineId,
  type ChatModelOption,
  type ClaudeAuth,
  type FastInfo,
  type PiInfo,
} from './models.ts';
import type { SettingsPayload } from '../../shared/api.ts';

/**
 * What the server can run a chat turn on, kept live.
 *
 * `GET /api/settings` reports both engines: `claude` (the CLI + credential, plus
 * the ⚙ "Default model") and `fastChat` (the optional metered model, present
 * only when an env key is configured). Saving the ⚙ drawer fires
 * `studio:settings-changed` with the fresh payload, so a picker re-seeds without
 * a refetch or a reload — that event is why this is a subscription rather than a
 * one-shot fetch in each component.
 *
 * Two consumers: the command bar's pill, and the modals that fire a turn on the
 * user's behalf (Merge, Change template). They shared none of this before and
 * the modals simply inherited whatever the pill held.
 */

/** One engine's reasoning ladder: the rung in force with no override, and the
 *  rungs the pill may offer for one message. */
export type EngineEffort = { effort: string | null; efforts: string[] };

export type ModelChoice = {
  claudeAvailable: boolean;
  /** Whose credential a Claude turn spends. A server setting, not a picker
   *  choice — but the pill has to say when a message will be charged. */
  claudeAuth: ClaudeAuth | string;
  fastInfo: FastInfo | null;
  /** The Pi agent, present only when the server switch is on and the engine is
   *  configured. Always metered — there is no auth field to read. */
  piInfo: PiInfo | null;
  offerSonnet: boolean;
  /** Where the pill STARTS: the ⚙ "Chat engine" preference, resolved to a value
   *  the pill actually offers — 'opus'/'sonnet' when that engine is Claude, else
   *  'fast'/'pi'. */
  defaultChoice: string;
  efforts: Partial<Record<ChatEngineId, EngineEffort>>;
  options: ChatModelOption[];
};

export function useModelChoice(): ModelChoice {
  const [claudeAvailable, setClaudeAvailable] = useState(false);
  // Whose credential a Claude turn spends. Not a picker choice — it is a server
  // setting — but the pill has to say when a message will be charged.
  const [claudeAuth, setClaudeAuth] = useState<ClaudeAuth | string>('subscription');
  const [fastInfo, setFastInfo] = useState<FastInfo | null>(null);
  // The Pi agent, present only when the server switch is on and the engine can
  // start (an endpoint, a key and a model, or a login and a model). Its auth
  // mode travels with it: on this engine the mode also says which PROVIDER is
  // running, and the pill's money mark keys off it.
  const [piInfo, setPiInfo] = useState<PiInfo | null>(null);
  // The ⚙ "Offer Claude Sonnet" toggle. Off by default — the server reports it
  // resolved (`claude.offerSonnet`) so this never re-applies the rule itself.
  const [offerSonnet, setOfferSonnet] = useState(false);
  // Where the pill STARTS: the ⚙ "Chat engine" preference, resolved to a value
  // the pill actually offers — 'opus'/'sonnet' when that engine is Claude, else
  // 'fast'/'pi'. It was Claude-only until 2026-08-03, so a studio that wanted
  // every message on another engine had to pick it again in the pill every time.
  const [defaultChoice, setDefaultChoice] = useState<string>('opus');
  // Per-engine `{ effort, efforts }` — the rung in force with no override, and
  // the rungs the pill may offer for one message. Empty until the first fetch,
  // which is the same as "no dial": the pill shows no effort row rather than a
  // guessed one.
  const [efforts, setEfforts] = useState<Partial<Record<ChatEngineId, EngineEffort>>>({});

  useEffect(() => {
    let alive = true;
    const apply = (d: SettingsPayload | null | undefined) => {
      if (!alive || !d) return;
      // Null/false = the default engine is unavailable (CLI or credential
      // missing) — turns then degrade to the OpenCode model server-side when one
      // exists, so this gates the PICKER, not the send.
      setClaudeAvailable(Boolean(d.claude?.available && d.claude?.authPresent));
      setOfferSonnet(Boolean(d.claude?.offerSonnet));
      setClaudeAuth(d.chat?.engines?.claude?.auth || 'subscription');
      // The server has already retired a saved 'sonnet' default when the toggle
      // is off, and already reduced a chat engine that can't run back to Claude,
      // so trust both rather than re-deriving either here.
      const engine = d.chat?.engine || 'claude';
      setDefaultChoice(engine === 'claude' ? (d.claude?.model === 'sonnet' ? 'sonnet' : 'opus') : engine);
      setFastInfo(
        d.fastChat?.available ? { provider: d.fastChat.provider ?? '', model: d.fastChat.model ?? null } : null,
      );
      // Each engine's reasoning ladder, for the pill's per-message effort row.
      // Server-derived and per MODEL (llm/chat-efforts.ts), never a list this
      // client keeps: deepseek-v4-flash has two rungs, other models six, and the pill
      // offering a rung the model coerces is the dial reading as broken. An
      // engine with no `efforts` gets no row at all.
      const engines = d.chat?.engines || {};
      setEfforts({
        claude: { effort: engines.claude?.effort ?? null, efforts: engines.claude?.efforts ?? [] },
        fast: { effort: engines.fast?.effort ?? null, efforts: engines.fast?.efforts ?? [] },
        pi: { effort: engines.pi?.effort ?? null, efforts: engines.pi?.efforts ?? [] },
      });
      setPiInfo(
        d.piChat?.available
          ? {
              model: d.piChat.model,
              effort: d.piChat.effort,
              // 'api' is Pi's default, unlike every other engine's — see
              // getPiAuth on the server.
              auth: d.chat?.engines?.pi?.auth || 'api',
              subscriptionProvider: d.chat?.engines?.pi?.subscriptionProvider || null,
            }
          : null,
      );
    };
    api.get<SettingsPayload>('/api/settings').then(apply, () => {});
    const onChanged = (e: WindowEventMap['studio:settings-changed']) => apply(e.detail);
    window.addEventListener('studio:settings-changed', onChanged);
    return () => {
      alive = false;
      window.removeEventListener('studio:settings-changed', onChanged);
    };
  }, []);

  return {
    claudeAvailable,
    claudeAuth,
    fastInfo,
    piInfo,
    offerSonnet,
    defaultChoice,
    efforts,
    options: chatModelOptions({ claudeAvailable, claudeAuth, fastInfo, piInfo, offerSonnet }),
  };
}
